import { useState, useEffect, useCallback, useRef } from 'react';
import { BolsaDetalle, TipoMarcacion, LineaLocalState, ColorVariante, EsquemaMarcacion, MarcacionOpcionFE, LineaCotizacion, LineaGuardada } from './types';
import { seleccionarOpcion, calcularPrecioMarcacion as calcularTotalMarcacion, calcularPrecioUnitario as calcularUnitarioMarcacion } from './utils/calcularPrecioMarcacion';
import { opcionesHermanasDe } from './utils/opcionesHermanas';
import { cotizacionDetalleService } from './service';
import {
  calcularPrecioUnitario,
  calcularPrecioVenta,
  calcularUtilidad,
  precioProveedorConMarcacion,
  TRANSPORTE_DEFAULT,
  seleccionarTramoDescuento,
  costoConDescuento,
  descuentoDelColor,
} from './domain/pricing';
import { EstadoBolsa } from '../CentroCotizaciones/state/bolsaMachine';

export { calcularPrecioUnitario, calcularPrecioVenta, calcularUtilidad, precioProveedorConMarcacion };

// SPEC-195 (UC-001, RN-002/RN-003) — % del tramo que contiene el monto de la línea (precio de lista
// × cantidad) y costo con descuento resultante. Sin tramos, la línea conserva su % y su costo
// (CA-019); en un hueco, 0 % y costo = precio de lista. Sin llamadas al backend (RNF-001).
// SPEC-207 (RN-015) — solo un % con origen `tramo` depende del monto: costo informado, neto,
// Outlet y general se conservan. Una línea anterior a la spec (sin origen) sigue como hoy.
function descuentoParaCantidad(
  s: LineaLocalState,
  linea: LineaCotizacion | undefined,
  cantidad: number,
): Pick<LineaLocalState, 'descuentoProveedor' | 'precioProveedorActual'> {
  const tramos = linea?.tramosDescuento ?? [];
  const origen = s.origenDescuento ?? null;
  if (!linea || tramos.length === 0 || (origen !== null && origen !== 'tramo')) {
    return { descuentoProveedor: s.descuentoProveedor, precioProveedorActual: s.precioProveedorActual };
  }
  const descuentoProveedor = seleccionarTramoDescuento(tramos, linea.precioUnitario * cantidad)?.porcentaje ?? 0;
  return { descuentoProveedor, precioProveedorActual: costoConDescuento(linea.precioUnitario, descuentoProveedor) };
}

// SPEC-206 (ADR-004) — líneas cuya marcación por tarifa se puede recalcular al cambiar la cantidad.
function marcacionesPorTarifa(lineas: LineaCotizacion[]): LineaCotizacion[] {
  return lineas.filter((l) =>
    (l.marcacionEsquema === 'minima' || l.marcacionEsquema === 'rango')
    && l.marcacionOpcionId != null && l.marcacionProveedorId != null && l.tipoMarcacionId != null);
}

export function useDetalleCotizacion(bolsaId: string | undefined) {
  const [bolsa, setBolsa] = useState<BolsaDetalle | null>(null);
  const [tiposMarcacion, setTiposMarcacion] = useState<TipoMarcacion[]>([]);
  const [lineasState, setLineasState] = useState<Record<string, LineaLocalState>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const [hayCambiosSinGuardar, setHayCambiosSinGuardar] = useState(false);

  // SPEC-167 — validez de construcción. `validez` es el valor mostrado (persistido o
  // sugerencia hoy+15); `validezError` refleja un rechazo de rango del backend.
  const [validez, setValidez] = useState<string | null>(null);
  const [validezError, setValidezError] = useState<string | null>(null);
  const [validezGuardando, setValidezGuardando] = useState(false);
  // Ref con la validez REALMENTE persistida (o null). Fuente de verdad síncrona para decidir
  // si hay que materializar la sugerencia al guardar (CA-004), sin depender del render.
  const validezPersistidaRef = useRef<string | null>(null);

  // Ref para acceder a bolsa dentro de callbacks sin dependencia reactiva
  const bolsaRef = useRef<BolsaDetalle | null>(null);
  useEffect(() => { bolsaRef.current = bolsa; }, [bolsa]);

  // SPEC-172 (RN-003, TASK-004): ref del estado de líneas para que los handlers que hoy leían
  // `lineasState`/`bolsa` en sus deps (recreándose en cada edición y rompiendo el React.memo de
  // TODAS las filas) puedan leerlos sin dependencia reactiva → referencia de callback estable.
  const lineasStateRef = useRef<Record<string, LineaLocalState>>({});
  useEffect(() => { lineasStateRef.current = lineasState; }, [lineasState]);

  useEffect(() => {
    if (!bolsaId) {
      setError('ID de bolsa no especificado');
      setLoading(false);
      return;
    }

    let cancelled = false;

    const cargar = async () => {
      setLoading(true);
      setError(null);
      try {
        const [bolsaData, tipos] = await Promise.all([
          cotizacionDetalleService.obtenerBolsa(bolsaId),
          cotizacionDetalleService.obtenerTiposMarcacion(),
        ]);

        if (cancelled) return;

        const estadoInicial: Record<string, LineaLocalState> = {};
        for (const linea of bolsaData.lineas) {
          const varianteInicial = linea.coloresVariantes.find(
            v => v.color === linea.colorSeleccionado,
          ) ?? linea.coloresVariantes[0];

          const pp = linea.precioConDescuento ?? linea.precioUnitario;
          const precioMarcacionInicial = linea.precioMarcacion ?? 0;
          const ppConMarcacion = pp + precioMarcacionInicial;
          const UTILIDAD_DEFAULT = 22;
          // SPEC-131 (RN-002): transporte y cantidad son de esta misma línea, no de la bolsa.
          const transporteLinea = linea.transporte ?? TRANSPORTE_DEFAULT;
          const precioUnitarioInicial = calcularPrecioUnitario(ppConMarcacion, transporteLinea, linea.cantidad);

          // Si ya existe precio guardado, derivar utilidad; si no, calcular con default 22%
          const pvGuardado = linea.precioVenta;
          let precioVentaInicial: number;
          let utilidadInicial: number;

          if (pvGuardado !== null && pvGuardado > 0) {
            precioVentaInicial = pvGuardado;
            utilidadInicial = calcularUtilidad(precioUnitarioInicial, pvGuardado);
          } else {
            utilidadInicial = UTILIDAD_DEFAULT;
            precioVentaInicial = Math.max(
              calcularPrecioVenta(precioUnitarioInicial, UTILIDAD_DEFAULT),
              precioUnitarioInicial,
            );
          }

          estadoInicial[linea.id] = {
            colorSeleccionado: linea.colorSeleccionado ?? (varianteInicial?.color ?? null),
            codigoColorSeleccionado: linea.codigoColorSeleccionado ?? (varianteInicial?.codigo_color ?? null),
            tipoMarcacionId: linea.tipoMarcacionId,
            marcacionOpcionId: linea.marcacionOpcionId ?? null,
            marcacionProveedorId: linea.marcacionProveedorId ?? null,
            precioMarcacion: linea.precioMarcacion ?? 0,
            // SPEC-206 (ADR-004) — esquema persistido; las opciones de tarifa se hidratan después.
            marcacionEsquema: linea.marcacionEsquema ?? null,
            marcacionOpcionesCache: [],
            cantidad: linea.cantidad,
            precioVenta: precioVentaInicial,
            transporte: transporteLinea,
            utilidad: utilidadInicial,
            precioProveedorActual: pp,
            descuentoProveedor: linea.descuentoProveedor,
            origenDescuento: linea.origenDescuento,
          };
        }

        setBolsa(bolsaData);
        setTiposMarcacion(tipos);
        setLineasState(estadoInicial);
        setHayCambiosSinGuardar(false);
        // SPEC-167 (RN-006) — mostrar la persistida si existe; si no, la sugerencia hoy+15.
        // No re-sugiere sobre una validez ya guardada (incluida una legacy fuera de rango, CA-010).
        setValidez(bolsaData.validez ?? bolsaData.validezSugerida ?? null);
        setValidezError(null);
        validezPersistidaRef.current = bolsaData.validez ?? null;
        hidratarOpciones(marcacionesPorTarifa(bolsaData.lineas), () => cancelled);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Error desconocido');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    // SPEC-206 (TC-035) — una lectura de tarifas por proveedor y tipo; si falla, la marcación queda
    // como antes de la spec (congelada) y nada se bloquea.
    const hidratarOpciones = async (lineas: LineaCotizacion[], cancelado: () => boolean) => {
      const configs = new Map<string, ReturnType<typeof cotizacionDetalleService.obtenerConfigMarcacion>>();
      for (const linea of lineas) {
        const clave = `${linea.marcacionProveedorId}-${linea.tipoMarcacionId}`;
        if (!configs.has(clave)) {
          configs.set(clave, cotizacionDetalleService.obtenerConfigMarcacion(linea.marcacionProveedorId!, linea.tipoMarcacionId!).catch(() => null));
        }
      }
      for (const linea of lineas) {
        const config = await configs.get(`${linea.marcacionProveedorId}-${linea.tipoMarcacionId}`);
        if (cancelado() || !config) continue;
        const opciones = opcionesHermanasDe(config, linea.marcacionOpcionId!);
        setLineasState(prev => (prev[linea.id] ? { ...prev, [linea.id]: { ...prev[linea.id], marcacionOpcionesCache: opciones } } : prev));
      }
    };

    cargar();
    return () => { cancelled = true; };
  }, [bolsaId]);

  const cambiarColor = useCallback((lineaId: string, color: string, codigoColor: string | null) => {
    setLineasState(prev => {
      const s = prev[lineaId];
      if (!s) return prev;

      const lineaData = bolsaRef.current?.lineas.find(l => l.id === lineaId);
      const varianteNueva = lineaData?.coloresVariantes.find(v => v.color === color);
      // SPEC-208 (RN-007): en una línea con opciones el precio de lista es el de la opción elegida,
      // no el del color.
      const precioListaNuevo = lineaData?.opcionesPrecio?.length
        ? lineaData.precioUnitario
        : varianteNueva?.precio ?? lineaData?.precioUnitario ?? null;
      // SPEC-195 (RN-002): el % vigente es el del tramo de la cantidad editada, no el de carga.
      const descuentoActual = s.descuentoProveedor ?? lineaData?.descuentoProveedor ?? 0;
      const origenActual = s.origenDescuento ?? null;
      // SPEC-207 (RN-011): el % y el origen del color elegido; si el color no los trae, los actuales.
      const delColor = descuentoDelColor(lineaData?.coloresVariantes, color);
      const descuento = delColor?.porcentaje ?? descuentoActual;
      const origen = delColor?.origen ?? origenActual;
      const nuevoPP = precioListaNuevo !== null
        ? costoConDescuento(precioListaNuevo, descuento)
        : s.precioProveedorActual;

      const nuevoPPConMarcacion = nuevoPP + s.precioMarcacion;
      const nuevoPrecioUnitario = calcularPrecioUnitario(nuevoPPConMarcacion, s.transporte, s.cantidad);
      let nuevoPV = s.precioVenta;
      if (s.precioVenta < nuevoPrecioUnitario) {
        // CA-012: auto-corregir manteniendo la misma utilidad%
        nuevoPV = Math.max(
          calcularPrecioVenta(nuevoPrecioUnitario, s.utilidad),
          nuevoPrecioUnitario,
        );
      }

      return {
        ...prev,
        [lineaId]: {
          ...s,
          colorSeleccionado: color,
          codigoColorSeleccionado: codigoColor,
          descuentoProveedor: descuento,
          origenDescuento: origen,
          precioProveedorActual: nuevoPP,
          precioVenta: nuevoPV,
        },
      };
    });
    setGuardado(false);
    setHayCambiosSinGuardar(true);
  }, []);

  // SPEC-208 (RN-007): elegir la opción i cambia el precio de lista de la línea a su valor; el % se
  // re-resuelve con el monto valor × cantidad (cambia de tramo si su origen es `tramo`, sigue en 0 si
  // es `neto`), el costo se recalcula y el precio de venta conserva la utilidad %. Sin llamadas al
  // backend (RNF-001): el índice se envía al guardar y el backend fija el precio desde su copia.
  const cambiarOpcion = useCallback((lineaId: string, indice: number) => {
    const lineaData = bolsaRef.current?.lineas.find(l => l.id === lineaId);
    const opcion = lineaData?.opcionesPrecio?.[indice];
    if (!lineaData || !opcion) return;
    const lineaConOpcion = { ...lineaData, precioUnitario: opcion.valor, opcionPrecioIndice: indice };

    setBolsa(prev => (prev ? { ...prev, lineas: prev.lineas.map(l => (l.id === lineaId ? lineaConOpcion : l)) } : prev));
    setLineasState(prev => {
      const s = prev[lineaId];
      if (!s) return prev;
      const descuentoProveedor = descuentoParaCantidad(s, lineaConOpcion, s.cantidad).descuentoProveedor ?? lineaData.descuentoProveedor;
      const conOpcion = { ...s, descuentoProveedor, precioProveedorActual: costoConDescuento(opcion.valor, descuentoProveedor) };
      const precioUnitario = calcularPrecioUnitario(precioProveedorConMarcacion(conOpcion), s.transporte, s.cantidad);
      const precioVenta = Math.max(calcularPrecioVenta(precioUnitario, s.utilidad), precioUnitario);
      return { ...prev, [lineaId]: { ...conOpcion, precioVenta } };
    });
    setGuardado(false);
    setHayCambiosSinGuardar(true);
  }, []);

  const cambiarMarcacion = useCallback((lineaId: string, tipoId: number | null) => {
    setLineasState(prev => ({
      ...prev,
      [lineaId]: { ...prev[lineaId], tipoMarcacionId: tipoId },
    }));
    setGuardado(false);
    setHayCambiosSinGuardar(true);
  }, []);

  const confirmarMarcacion = useCallback((
    lineaId: string,
    tipoId: number,
    proveedorId: number,
    opcionId: number | null,
    precio: number,
    esquema: EsquemaMarcacion | null = null,
    opcionesFiltradas: MarcacionOpcionFE[] = [],
  ) => {
    setLineasState(prev => {
      const s = prev[lineaId];
      if (!s) return prev;
      // RN-008: recalcular precio unitario/de venta con el nuevo pp combinado,
      // manteniendo la misma utilidad% (mismo patrón que cambiarColor).
      const pp = precioProveedorConMarcacion({ precioProveedorActual: s.precioProveedorActual, precioMarcacion: precio });
      const precioUnitario = calcularPrecioUnitario(pp, s.transporte, s.cantidad);
      const pv = Math.max(
        calcularPrecioVenta(precioUnitario, s.utilidad),
        precioUnitario,
      );
      return {
        ...prev,
        [lineaId]: {
          ...s,
          tipoMarcacionId: tipoId,
          marcacionOpcionId: opcionId,
          marcacionProveedorId: proveedorId,
          precioMarcacion: precio,
          // SPEC-096 (RN-003): cachea esquema y opciones para poder recalcular en cambiarCantidad
          // sin reabrir el modal ni llamar al backend.
          marcacionEsquema: esquema,
          marcacionOpcionesCache: opcionesFiltradas,
          precioVenta: pv,
        },
      };
    });
    setGuardado(false);
    setHayCambiosSinGuardar(true);
  }, []);

  // SPEC-096 (RN-001/RN-002/RN-004/RN-005): al cambiar cantidad, re-evalúa el término de
  // marcación cuando el esquema confirmado es 'minima' o 'rango', antes de recomputar
  // precio_unitario/precio_venta. 'area' y 'manual' quedan congelados (RN-004).
  // SPEC-131 (RN-002): cantidad y transporte son propios de esta línea — cambiarla solo
  // afecta el precio_unitario/precio_venta de ESTA línea (CA-012/TC-010 de SPEC-096 exige
  // aislamiento entre líneas al cambiar cantidad, ya certificado).
  const cambiarCantidad = useCallback((lineaId: string, cantidad: number) => {
    setLineasState(prev => {
      const s = prev[lineaId];
      if (!s) return prev;

      let precioMarcacion = s.precioMarcacion;
      let marcacionOpcionId = s.marcacionOpcionId;

      if (s.marcacionEsquema === 'minima' || s.marcacionEsquema === 'rango') {
        const nuevaOpcion = seleccionarOpcion(s.marcacionOpcionesCache, cantidad);
        // RN-005: sin tramo aplicable, se conserva el valor vigente
        if (nuevaOpcion) {
          const totalNuevo = calcularTotalMarcacion(nuevaOpcion, cantidad);
          precioMarcacion = calcularUnitarioMarcacion(totalNuevo, cantidad);
          marcacionOpcionId = nuevaOpcion.id;
        }
      }

      const lineaData = bolsaRef.current?.lineas.find(l => l.id === lineaId);
      const sConMarcacionActualizada = {
        ...s,
        precioMarcacion,
        marcacionOpcionId,
        cantidad,
        ...descuentoParaCantidad(s, lineaData, cantidad),
      };
      const pp = precioProveedorConMarcacion(sConMarcacionActualizada);
      const precioUnitario = calcularPrecioUnitario(pp, s.transporte, cantidad);
      const pv = Math.max(calcularPrecioVenta(precioUnitario, s.utilidad), precioUnitario);
      return { ...prev, [lineaId]: { ...sConMarcacionActualizada, precioVenta: pv } };
    });
    setGuardado(false);
    setHayCambiosSinGuardar(true);
  }, []);

  // SPEC-131 (RN-001, RN-002, CA-003): edita el transporte de UNA línea — recalcula solo esa
  // línea. Persistencia diferida (RN-003): viaja en el payload de guardarCotizacion, igual
  // que cantidad/color/precio de venta — no hay guardado inmediato como tenía bolsas.freight.
  const cambiarTransporte = useCallback((lineaId: string, transporte: number) => {
    setLineasState(prev => {
      const s = prev[lineaId];
      if (!s) return prev;
      const pp = precioProveedorConMarcacion(s);
      const precioUnitario = calcularPrecioUnitario(pp, transporte, s.cantidad);
      const pv = Math.max(calcularPrecioVenta(precioUnitario, s.utilidad), precioUnitario);
      return { ...prev, [lineaId]: { ...s, transporte, precioVenta: pv } };
    });
    setGuardado(false);
    setHayCambiosSinGuardar(true);
  }, []);

  const cambiarUtilidad = useCallback((lineaId: string, utilidad: number) => {
    setLineasState(prev => {
      const s = prev[lineaId];
      if (!s) return prev;
      const pp = precioProveedorConMarcacion(s);
      const precioUnitario = calcularPrecioUnitario(pp, s.transporte, s.cantidad);
      const pv = Math.max(
        calcularPrecioVenta(precioUnitario, utilidad),
        precioUnitario,
      );
      return { ...prev, [lineaId]: { ...s, utilidad, precioVenta: pv } };
    });
    setGuardado(false);
    setHayCambiosSinGuardar(true);
  }, []);

  // SPEC-165 (RN-001..RN-003, RN-006): aplica el mismo % de utilidad a TODAS las líneas de la
  // bolsa, recalculando el precio de venta de cada una con el dominio `pricing` (misma lógica que
  // cambiarUtilidad — prohibido duplicar la fórmula). Sobrescribe incluso las líneas editadas a
  // mano (RN-001). El precio unitario no cambia: solo se derivan utilidad y precioVenta (RN-003).
  // Persistencia diferida: marca hayCambiosSinGuardar; se guarda con "Guardar" (RN-006).
  const aplicarUtilidadMasiva = useCallback((utilidad: number) => {
    setLineasState(prev => {
      const next: Record<string, LineaLocalState> = {};
      for (const [id, s] of Object.entries(prev)) {
        const pp = precioProveedorConMarcacion(s);
        const precioUnitario = calcularPrecioUnitario(pp, s.transporte, s.cantidad);
        const pv = Math.max(calcularPrecioVenta(precioUnitario, utilidad), precioUnitario);
        next[id] = { ...s, utilidad, precioVenta: pv };
      }
      return next;
    });
    setGuardado(false);
    setHayCambiosSinGuardar(true);
  }, []);

  // SPEC-181 (RN-001..RN-003, RN-006): aplica la misma cantidad a TODAS las líneas de la bolsa,
  // re-evaluando marcación (esquemas 'minima'/'rango') y recalculando precio unitario/venta con la
  // misma lógica que cambiarCantidad (SPEC-096) — prohibido duplicar la fórmula ni la selección de
  // tramo. Sobrescribe incluso las líneas editadas a mano (RN-001). La utilidad de cada línea no
  // cambia (RN-003). Persistencia diferida: marca hayCambiosSinGuardar; se guarda con "Guardar".
  const aplicarCantidadMasiva = useCallback((cantidad: number) => {
    setLineasState(prev => {
      const next: Record<string, LineaLocalState> = {};
      for (const [id, s] of Object.entries(prev)) {
        let precioMarcacion = s.precioMarcacion;
        let marcacionOpcionId = s.marcacionOpcionId;

        if (s.marcacionEsquema === 'minima' || s.marcacionEsquema === 'rango') {
          const nuevaOpcion = seleccionarOpcion(s.marcacionOpcionesCache, cantidad);
          if (nuevaOpcion) {
            const totalNuevo = calcularTotalMarcacion(nuevaOpcion, cantidad);
            precioMarcacion = calcularUnitarioMarcacion(totalNuevo, cantidad);
            marcacionOpcionId = nuevaOpcion.id;
          }
        }

        const lineaData = bolsaRef.current?.lineas.find(l => l.id === id);
        const sConMarcacionActualizada = {
          ...s,
          precioMarcacion,
          marcacionOpcionId,
          cantidad,
          ...descuentoParaCantidad(s, lineaData, cantidad),
        };
        const pp = precioProveedorConMarcacion(sConMarcacionActualizada);
        const precioUnitario = calcularPrecioUnitario(pp, s.transporte, cantidad);
        const pv = Math.max(calcularPrecioVenta(precioUnitario, s.utilidad), precioUnitario);
        next[id] = { ...sConMarcacionActualizada, precioVenta: pv };
      }
      return next;
    });
    setGuardado(false);
    setHayCambiosSinGuardar(true);
  }, []);

  const cambiarPrecioVenta = useCallback((lineaId: string, precio: number) => {
    setLineasState(prev => {
      const s = prev[lineaId];
      if (!s) return prev;
      const pp = precioProveedorConMarcacion(s);
      const precioUnitario = calcularPrecioUnitario(pp, s.transporte, s.cantidad);
      const pvFinal = Math.max(precio, precioUnitario);
      // RN-003: recalcular utilidad con fórmula inversa sobre el precio unitario completo
      const nuevaUtilidad = calcularUtilidad(precioUnitario, pvFinal);
      return { ...prev, [lineaId]: { ...s, precioVenta: pvFinal, utilidad: nuevaUtilidad } };
    });
    setGuardado(false);
    setHayCambiosSinGuardar(true);
  }, []);

  const marcarCambioPendiente = useCallback(() => {
    setHayCambiosSinGuardar(true);
  }, []);

  const getVarianteActual = useCallback((lineaId: string, coloresVariantes: ColorVariante[]) => {
    const state = lineasState[lineaId];
    if (!state) return coloresVariantes[0] ?? null;
    return coloresVariantes.find(v => v.color === state.colorSeleccionado) ?? coloresVariantes[0] ?? null;
  }, [lineasState]);

  // SPEC-162 (RN-001..RN-005): persiste el estado editado (lineasState de TODAS las líneas)
  // antes de duplicar. Reporta éxito/fallo con Promise<boolean> para que duplicarLinea pueda
  // abortar si el guardado falla (RN-004). Los llamadores previos (botón "Guardar", crearPdf
  // en BORRADOR) ignoran el retorno → comportamiento idéntico para ellos.
  // SPEC-167 (UC-002/003, CA-002/CA-003) — persiste la validez elegida. Optimista en el valor
  // mostrado; ante rechazo de rango (400) revierte al último persistido y expone el mensaje.
  const actualizarValidez = useCallback(async (fecha: string) => {
    if (!bolsaId) return;
    const anterior = validezPersistidaRef.current;
    setValidez(fecha);
    setValidezGuardando(true);
    setValidezError(null);
    try {
      const guardada = await cotizacionDetalleService.actualizarValidez(bolsaId, fecha);
      const valor = guardada ?? fecha;
      setValidez(valor);
      validezPersistidaRef.current = valor;
      setBolsa(prev => (prev ? { ...prev, validez: valor } : prev));
    } catch (err) {
      // Rechazo (fuera de rango): no persiste; revertir el valor mostrado al último válido.
      setValidez(anterior);
      setValidezError(err instanceof Error ? err.message : 'Error al guardar la fecha de validez');
    } finally {
      setValidezGuardando(false);
    }
  }, [bolsaId]);

  // SPEC-195 (RN-004, CA-023) — el backend es la fuente de verdad del tramo: tras guardar, la línea
  // adopta el %, el costo, la cantidad y el subtotal persistidos, y la bolsa el total, sin recargar.
  // El precio de venta es el que se acaba de guardar; si el costo cambió, la utilidad se deriva de
  // él igual que al cargar la bolsa.
  const adoptarLoGuardado = useCallback((guardado: { total?: number | null; lineas?: LineaGuardada[] }) => {
    const lineasGuardadas = guardado.lineas ?? [];
    if (lineasGuardadas.length === 0) return;
    const porId = new Map(lineasGuardadas.map(l => [l.id, l]));

    setBolsa(prev => (prev ? {
      ...prev,
      total: guardado.total ?? prev.total,
      lineas: prev.lineas.map(l => {
        const g = porId.get(l.id);
        return g ? {
          ...l,
          cantidad: g.cantidad,
          // SPEC-208 (RN-008) — el precio de lista que fijó el backend desde su copia de opciones.
          ...(g.precioUnitario !== undefined && { precioUnitario: g.precioUnitario }),
          descuentoProveedor: g.descuentoProveedor,
          precioConDescuento: g.precioConDescuento,
          ...(g.origenDescuento !== undefined && { origenDescuento: g.origenDescuento }),
          subtotal: g.subtotal,
        } : l;
      }),
    } : prev));

    setLineasState(prev => {
      const next = { ...prev };
      for (const g of lineasGuardadas) {
        const s = prev[g.id];
        const linea = bolsaRef.current?.lineas.find(l => l.id === g.id);
        if (!s || !linea) continue;
        const costo = g.precioConDescuento ?? linea.precioUnitario;
        const ajustada = {
          ...s,
          descuentoProveedor: g.descuentoProveedor,
          ...(g.origenDescuento !== undefined && { origenDescuento: g.origenDescuento }),
          precioProveedorActual: costo,
        };
        if (costo !== s.precioProveedorActual) {
          const precioUnitario = calcularPrecioUnitario(precioProveedorConMarcacion(ajustada), s.transporte, s.cantidad);
          ajustada.utilidad = calcularUtilidad(precioUnitario, s.precioVenta);
        }
        next[g.id] = ajustada;
      }
      return next;
    });
  }, []);

  const guardarCotizacion = useCallback(async (): Promise<boolean> => {
    if (!bolsaId || !bolsa) return false;
    setGuardando(true);
    try {
      const lineas = bolsa.lineas.map(linea => {
        const state = lineasState[linea.id] ?? {
          colorSeleccionado: linea.colorSeleccionado,
          codigoColorSeleccionado: linea.codigoColorSeleccionado,
          tipoMarcacionId: linea.tipoMarcacionId,
          cantidad: linea.cantidad,
          precioVenta: linea.precioVenta ?? linea.precioUnitario,
          transporte: linea.transporte ?? TRANSPORTE_DEFAULT,
        };
        return {
          id: linea.id,
          color_seleccionado: state.colorSeleccionado,
          codigo_color_seleccionado: state.codigoColorSeleccionado,
          tipo_marcacion_id: state.tipoMarcacionId,
          precio_venta: state.precioVenta,
          cantidad: state.cantidad,
          transporte: state.transporte,
          marcacion_opcion_id: state.marcacionOpcionId ?? null,
          marcacion_precio: state.precioMarcacion > 0 ? state.precioMarcacion : null,
          marcacion_proveedor_id: state.marcacionProveedorId ?? null,
          // SPEC-206 (CA-035) — sin marcación el esquema viaja en null.
          marcacion_esquema: state.tipoMarcacionId != null ? (state.marcacionEsquema ?? linea.marcacionEsquema ?? null) : null,
          // SPEC-208 (RN-008, RNF-006) — solo el índice, y solo en las líneas con copia de opciones.
          ...(!!linea.opcionesPrecio?.length && linea.opcionPrecioIndice != null && { opcion_precio_indice: linea.opcionPrecioIndice }),
        };
      });
      const resultadoGuardado = await cotizacionDetalleService.guardarCotizacion(bolsaId, lineas);
      adoptarLoGuardado(resultadoGuardado);
      // SPEC-182 (UC-004/RN-007) — si el guardado convirtió la bolsa en cotización, refleja
      // estadoCodigo/codigoCotizacion en el estado local; el efecto ya existente en index.tsx
      // (SPEC-133) sincroniza bolsasAbiertas/BagStrip a partir de estos mismos campos, sin
      // refetch completo.
      if (resultadoGuardado.convertida) {
        setBolsa(prev => (prev ? {
          ...prev,
          estadoCodigo: EstadoBolsa.BORRADOR,
          codigoCotizacion: resultadoGuardado.codigoCotizacion,
        } : prev));
      }
      // SPEC-167 (UC-004/CA-004) — si la validez sigue sin persistir, materializar la sugerencia
      // mostrada (hoy+15) al guardar. Idempotente: si el usuario ya la editó, la ref no es null.
      if (validezPersistidaRef.current == null) {
        const aMaterializar = validez ?? bolsa.validezSugerida ?? null;
        if (aMaterializar) {
          try {
            const guardada = await cotizacionDetalleService.actualizarValidez(bolsaId, aMaterializar);
            const valor = guardada ?? aMaterializar;
            validezPersistidaRef.current = valor;
            setValidez(valor);
            setBolsa(prev => (prev ? { ...prev, validez: valor } : prev));
          } catch (err) {
            // No bloquear el guardado de líneas/precios por esto, pero tampoco ocultarlo:
            // un fallo aquí deja `validez` en null en BD, lo que más tarde bloquea "Enviar"
            // con un error que no coincide con la fecha que sí se ve en el PDF (fallback
            // legacy `emisión + vigencia_dias`, independiente de `bolsas.validez`). Reportar
            // en `validezError` para que el usuario lo vea junto al control, en vez de un
            // "Guardado" silencioso que esconde que la validez no quedó persistida.
            setValidezError(
              err instanceof Error ? err.message : 'No se pudo guardar la fecha de validez'
            );
          }
        }
      }
      setGuardado(true);
      setHayCambiosSinGuardar(false);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
      return false;
    } finally {
      setGuardando(false);
    }
  }, [bolsaId, bolsa, lineasState, validez, adoptarLoGuardado]);

  const duplicarLinea = useCallback(async (lineaId: string) => {
    if (!bolsaId || !bolsa) return;

    // SPEC-162 (RN-001/RN-004): con persistencia diferida (SPEC-131), el estado editado vive
    // solo en lineasState hasta "Guardar". El backend de duplicar re-lee el original DESDE LA BD
    // (sin body), así que si hay cambios sin guardar hay que persistirlos ANTES de duplicar, de
    // forma secuencial. Si el guardado falla, se aborta la duplicación (no queda duplicado huérfano).
    if (hayCambiosSinGuardar) {
      const guardado = await guardarCotizacion();
      if (!guardado) return; // RN-004: el error ya quedó en setError
    }

    try {
      const nuevaLinea = await cotizacionDetalleService.duplicarLinea(bolsaId, lineaId);

      setBolsa(prev => {
        if (!prev) return prev;
        const idx = prev.lineas.findIndex(l => l.id === lineaId);
        const nuevasLineas = [...prev.lineas];
        nuevasLineas.splice(idx + 1, 0, nuevaLinea);
        return { ...prev, lineas: nuevasLineas, lineas_count: prev.lineas_count + 1 };
      });

      setLineasState(prev => {
        const estadoOriginal = prev[lineaId];
        if (!estadoOriginal) return prev;
        return { ...prev, [nuevaLinea.id]: { ...estadoOriginal } };
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al duplicar');
    }
  }, [bolsaId, bolsa, hayCambiosSinGuardar, guardarCotizacion]);

  // SPEC-172 (RN-003): lee bolsa/lineasState desde refs para mantener una referencia ESTABLE
  // (deps solo [bolsaId]); antes dependía de [bolsaId, bolsa, lineasState] y se recreaba en cada
  // edición, re-renderizando todas las ProductRowCard vía la prop onBorrar.
  const eliminarLinea = useCallback(async (lineaId: string) => {
    const bolsaActual = bolsaRef.current;
    if (!bolsaId || !bolsaActual) return;

    const snapshotLineas = bolsaActual.lineas;
    const snapshotLineasState = lineasStateRef.current;
    const snapshotCount = bolsaActual.lineas_count;

    setBolsa(prev => prev ? {
      ...prev,
      lineas: prev.lineas.filter(l => l.id !== lineaId),
      lineas_count: prev.lineas_count - 1,
    } : prev);
    setLineasState(prev => {
      const next = { ...prev };
      delete next[lineaId];
      return next;
    });

    try {
      await cotizacionDetalleService.eliminarLinea(bolsaId, lineaId);
    } catch (err) {
      setBolsa(prev => prev ? {
        ...prev,
        lineas: snapshotLineas,
        lineas_count: snapshotCount,
      } : prev);
      setLineasState(snapshotLineasState);
      setError(err instanceof Error ? err.message : 'Error al eliminar la línea');
    }
  }, [bolsaId]);

  const agregarProducto = useCallback(async (productoId: string, imagenUrl?: string | null): Promise<void> => {
    if (!bolsaId || !bolsa) return;
    await cotizacionDetalleService.agregarLinea(bolsa.id, {
      producto_id: productoId,
      cantidad: 1,
      imagen_url: imagenUrl ?? undefined,
    });
    const bolsaActualizada = await cotizacionDetalleService.obtenerBolsa(bolsa.id);
    const estadoNuevo: Record<string, LineaLocalState> = { ...lineasState };
    const UTILIDAD_DEFAULT = 22;
    for (const linea of bolsaActualizada.lineas) {
      if (!estadoNuevo[linea.id]) {
        const varianteInicial = linea.coloresVariantes[0];
        const pp = linea.precioConDescuento ?? linea.precioUnitario;
        // SPEC-131 (CA-001): la línea nace con transporte = 60000 (default de columna).
        const transporteLinea = linea.transporte ?? TRANSPORTE_DEFAULT;
        const precioUnitarioInicial = calcularPrecioUnitario(pp, transporteLinea, linea.cantidad);
        estadoNuevo[linea.id] = {
          colorSeleccionado: varianteInicial?.color ?? null,
          codigoColorSeleccionado: varianteInicial?.codigo_color ?? null,
          tipoMarcacionId: null,
          marcacionOpcionId: null,
          marcacionProveedorId: null,
          precioMarcacion: 0,
          marcacionEsquema: null,
          marcacionOpcionesCache: [],
          cantidad: 1,
          precioVenta: Math.max(
            calcularPrecioVenta(precioUnitarioInicial, UTILIDAD_DEFAULT),
            precioUnitarioInicial,
          ),
          transporte: transporteLinea,
          utilidad: UTILIDAD_DEFAULT,
          precioProveedorActual: pp,
          descuentoProveedor: linea.descuentoProveedor,
          origenDescuento: linea.origenDescuento,
        };
      }
    }
    setBolsa(bolsaActualizada);
    setLineasState(estadoNuevo);
  }, [bolsaId, bolsa, lineasState]);

  // SPEC-133 (RN-001): bifurca según el estado de la bolsa — ACTIVA dispara la conversión
  // (genera el PDF inicial, SPEC-126 sin cambios); BORRADOR reutiliza el guardado existente,
  // que ya regenera el PDF como side-effect (SPEC-126 UC-002).
  const crearPdf = useCallback(async () => {
    if (!bolsaId || !bolsa) return;
    if (bolsa.estadoCodigo === EstadoBolsa.ACTIVA) {
      setGuardando(true);
      setError(null);
      try {
        const bolsaActualizada = await cotizacionDetalleService.convertirBolsa(bolsaId);
        setBolsa(bolsaActualizada);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al crear el PDF');
      } finally {
        setGuardando(false);
      }
      return;
    }
    await guardarCotizacion();
  }, [bolsaId, bolsa, guardarCotizacion]);

  // RN-005: subtotal = Σ(cantidad × precio_venta) desde estado local
  const subtotal = bolsa?.lineas.reduce((acc, linea) => {
    const state = lineasState[linea.id];
    const pv = state?.precioVenta ?? linea.precioUnitario;
    const qty = state?.cantidad ?? linea.cantidad;
    return acc + pv * qty;
  }, 0) ?? 0;

  return {
    bolsa,
    tiposMarcacion,
    lineasState,
    loading,
    error,
    guardando,
    guardado,
    hayCambiosSinGuardar,
    subtotal,
    // SPEC-167 — validez de construcción: valor mostrado, sugerencia (min/max), estado y acción.
    validez,
    validezSugerida: bolsa?.validezSugerida ?? null,
    validezError,
    validezGuardando,
    actualizarValidez,
    cambiarColor,
    cambiarOpcion,
    cambiarMarcacion,
    confirmarMarcacion,
    cambiarCantidad,
    cambiarTransporte,
    cambiarUtilidad,
    aplicarUtilidadMasiva,
    aplicarCantidadMasiva,
    cambiarPrecioVenta,
    marcarCambioPendiente,
    getVarianteActual,
    guardarCotizacion,
    crearPdf,
    duplicarLinea,
    eliminarLinea,
    agregarProducto,
  };
}
